import NextImage from 'next/image';
import { Fragment, useContext } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { Trash } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useTheme } from 'styled-components';

import { transformProductToGTMItem } from '@tmh-website/services/gtm.service';
import { CustomerCartItem, removeItemFromCart } from '@tmh-website/services/magento/cart-customer.service';
import { transformGraphQlProductToProduct } from '@tmh-website/services/magento/graphql.service';
import { formatCurrency } from '@tmh-website/services/price.client.service';
import { CustomerCartContext } from '@tmh-website/shared';
import { Color } from '@tmh-website/shared/colors';
import { LocalePrefix, PrefixToCurrencyMap } from '@tmh-website/shared/const';
import { isNonEmptyTrimmedString } from '@tmh-website/shared/helpers';
import StyledTypo from '@tmh-website/styles/typo';

import * as Styled from './MiniCartEntry.styles';

interface MiniCartEntryProps {
  locale: LocalePrefix;
  item: CustomerCartItem;
  isStandaloneMode: boolean;
}

const MiniCartEntry = ({ item, locale, isStandaloneMode }: MiniCartEntryProps) => {
  const { cart, refetch } = useContext(CustomerCartContext);
  const { t } = useTranslation();
  const { textOpacity } = useTheme();

  const imageSrc: string | null | undefined = isNonEmptyTrimmedString(item.product.configured_variant?.small_image?.url)
    ? item.product.configured_variant?.small_image?.url
    : item.product.small_image?.url;

  const { mutate: removeItem, isLoading: isRemoving } = useMutation(
    () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'remove_from_cart',
          currency: PrefixToCurrencyMap[locale],
          value: (item.product.price.reduced ?? item.product.price.regular) * item.quantity,
          items: transformProductToGTMItem(transformGraphQlProductToProduct(item.product), { quantity: item.quantity }),
        },
      });
      return removeItemFromCart({ cartId: cart!.id, cartItemUid: item.uid, locale });
    },
    {
      onSuccess: () => refetch(),
      onError: () => refetch(),
    }
  );

  return (
    <Styled.Container key={item.uid}>
      <Styled.MainContainer>
        {isNonEmptyTrimmedString(imageSrc) && (
          <Styled.ImageWrapper>
            <Styled.AdjustedImage
              as={isStandaloneMode ? 'img' : NextImage}
              width={100}
              height={100}
              src={imageSrc}
              alt={item.product.name}
            />
          </Styled.ImageWrapper>
        )}
        <Styled.ContentContainer>
          <Styled.DescriptionContainer>
            <Styled.ProductName as={item.product.url ? 'a' : 'p'} href={item.product.url}>
              {item.product.name}
            </Styled.ProductName>
            {item.options!.length > 0 && (
              <StyledTypo.Typography $style='uiText' $opacity={textOpacity} $color={Color.Blue_1000_Deep}>
                {item.options?.map(option => (
                  <Fragment key={option.label}>
                    <span>
                      {option.label}: {option.value}
                    </span>
                    <br />
                  </Fragment>
                ))}
              </StyledTypo.Typography>
            )}
          </Styled.DescriptionContainer>
          <Styled.ActionBarContainer>
            <Styled.Price>{formatCurrency({ amount: item.rowPriceIncludingTax, locale })}</Styled.Price>

            <Styled.Quantity $style='uiText' $color={Color.Blue_1000_Deep}>
              {t('global:cart.quantity', { count: item.quantity })}
            </Styled.Quantity>

            <Styled.ButtonRemove
              aria-label={t('global:cart.removeFromCart') as string}
              themeColor={Color.Neutral_200_OffWhite}
              isTransparent
              isLoading={isRemoving}
              onClick={() => removeItem()}
              phosphorPadding>
              <Trash size={20} />
            </Styled.ButtonRemove>
          </Styled.ActionBarContainer>
        </Styled.ContentContainer>
      </Styled.MainContainer>
    </Styled.Container>
  );
};

export default MiniCartEntry;
